import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

import { useDispatch, useSelector } from "react-redux";
import CustomTable from "src/components/Table/Table";
import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";

import {
  getCamerasListAction,
  addCameraAction,
  deleteCameraAction,
  updateCameraStatusAction,
  getCaptainsDropdownAction,
  assignCaptainToCameraAction,
} from "src/api/cameras";

export default function Cameras() {
  const dispatch = useDispatch();
  const { camerasList } = useSelector((state: any) => state.cameras);

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);

  const [captains, setCaptains] = useState<any[]>([]);

  const [form, setForm] = useState({
    cameraNumber: "",
    cameraKey: "",
    simNumber: "",
    driverId: "",
    validity: "",
    lastRecharge: "",
  });

  const fetchData = async () => {
    setLoading(true);
    await dispatch(getCamerasListAction({ pageNo: 1, pageSize: 10 }));
    setLoading(false);
  };

  const fetchCaptains = async () => {
    const res = await dispatch(getCaptainsDropdownAction());
    setCaptains(res || []);
  };

  useEffect(() => {
    fetchData();
    fetchCaptains();
  }, []);

  const handleSubmit = async () => {
    await dispatch(
      addCameraAction({
        ...form,
        // ✅ FIX DATE FORMAT
        lastRecharge: form.lastRecharge
          ? new Date(form.lastRecharge).toISOString()
          : null,
      })
    );

    setOpenDialog(false);
    fetchData();
  };

  const confirmDelete = async () => {
    await dispatch(deleteCameraAction(deleteItem.id));
    setDeleteItem(null);
    fetchData();
  };

  const handleAssignCaptain = async (row: any, captainId: string) => {
    await dispatch(
      assignCaptainToCameraAction({
        cameraId: row.id,
        captainId,
      })
    );
    fetchData();
  };

  const columns = [
    { field: "cameraNumber", headerName: "Camera Number", minWidth: 150 },
    { field: "cameraKey", headerName: "Camera Key", minWidth: 150 },
    { field: "simNumber", headerName: "SIM Number", minWidth: 150 },
    { field: "validity", headerName: "Validity", minWidth: 120 },
    { field: "lastRecharge", headerName: "Last Recharge", minWidth: 180 },
    { field: "captain", headerName: "Captain", minWidth: 180 },
    { field: "status", headerName: "Status", minWidth: 120 },
    { field: "action", headerName: "Action", minWidth: 120 },
  ];

  const actionContent = (column: any, row: any) => {
    if (column === "captain") {
      return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={row.driverId || ""}
            onChange={(e) =>
              handleAssignCaptain(row, e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <span style={{ color: "#9ca3af" }}>Assign Captain</span>
            </MenuItem>

            {captains.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column === "action") {
      return (
        <IconButton onClick={() => setDeleteItem(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      );
    }

    return (
      <Typography>
        {column === "lastRecharge" && row.lastRecharge
          ? new Date(row.lastRecharge).toLocaleDateString("en-IN")
          : row[column] || "-"}
      </Typography>
    );
  };

  return (
    <>
      {loading && <CustomBackdrop open />}

      <Box>
         <Box sx={{ mt: "30px", p: 3, display: "flex",
                  alignItems: "center",
                  mb: -2,  }}>

          <Button
            variant="outlined"
            startIcon={<PersonAddAltIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ alignSelf: "flex-start" }}
          >
            Add Camera
          </Button>
        </Box>

        <CustomTable
          rows={camerasList?.data || []}
          columns={columns}
          total={camerasList?.total || 0}
          fetchData={() => {}}
          resetPagination={() => {}}
          action={actionContent}
        />
      </Box>

      {/* ADD CAMERA */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Camera</DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Camera Number"
              onChange={(e) =>
                setForm({ ...form, cameraNumber: e.target.value })
              }
            />

            <TextField
              label="Camera Key"
              onChange={(e) =>
                setForm({ ...form, cameraKey: e.target.value })
              }
            />

            <TextField
              label="SIM Number"
              onChange={(e) =>
                setForm({ ...form, simNumber: e.target.value })
              }
            />

            <TextField
              label="Validity"
              onChange={(e) =>
                setForm({ ...form, validity: e.target.value })
              }
            />

            <TextField
              type="date"
              label="Last Recharge"
              InputLabelProps={{ shrink: true }}
              onChange={(e) =>
                setForm({ ...form, lastRecharge: e.target.value })
              }
            />

            <FormControl>
              <Select
                value={form.driverId}
                displayEmpty
                onChange={(e) =>
                  setForm({ ...form, driverId: e.target.value })
                }
              >
                <MenuItem value="">
                  <span style={{ color: "#9ca3af" }}>Select Captain</span>
                </MenuItem>

                {captains.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>

              <Button variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* DELETE */}
      <Dialog open={!!deleteItem} onClose={() => setDeleteItem(null)}>
        <DialogTitle>Delete Confirmation</DialogTitle>

        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to delete{" "}
            <b>{deleteItem?.cameraNumber}</b>?
          </Typography>

          <Box display="flex" justifyContent="flex-end">
            <Button onClick={() => setDeleteItem(null)}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}